import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { NavigationListItem } from '@/types';
import {
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { ListBox, ScrollableList, listButtonStyles, listStyles } from '../styles';
import { SearchField } from '../SearchField';
import { useMemo, useState } from 'react';
import { abyssColors } from '@/theme/colors';

type NavigationPanelProps = {
  itemType: string;
  items: NavigationListItem[];
  selectedItemId: string;
  showCount?: boolean;
  searchDisabled?: boolean;
  showTotalHeader?: boolean;
};

export const NavigationPanel = ({
  items,
  selectedItemId,
  itemType = 'Item',
  searchDisabled = false,
  showCount = false,
  showTotalHeader = false,
}: NavigationPanelProps) => {
  const [itemSearchTerm, setItemSearchTerm] = useState<string>('');
  const { leftDrawerWidth } = useDrawerWidths();

  const filteredItems = useMemo(() => {
    if (!itemSearchTerm) return items;
    return items.filter(({ value }) => value.toLowerCase().includes(itemSearchTerm.toLowerCase()));
  }, [itemSearchTerm, items]);

  return (
    <ListBox
      sx={{
        padding: { md: 1, lg: 2 },
        maxWidth: leftDrawerWidth,
      }}
      data-testid={`${itemType.toLowerCase()}s-list`}
    >
      <ScrollableList>
        <List sx={listStyles}>
          {!searchDisabled && (
            <ListItem sx={{ px: 0, pb: 2 }}>
              <SearchField
                searchTerm={itemSearchTerm}
                setSearchTerm={setItemSearchTerm}
                searchPlaceholder={`Search ${itemType}s`}
              />
              <Divider sx={{ mb: 1 }} />
            </ListItem>
          )}
          {showTotalHeader && (
            <>
              <ListItem key={`all-${itemType}s`} disablePadding>
                <ListItemButton sx={listButtonStyles}>
                  <ListItemText
                    primary={`All ${itemType}s`}
                    sx={{ color: abyssColors.primary[500] }}
                  />
                  {showCount && (
                    <Typography
                      data-testid={`${itemType.toLowerCase()}s-count`}
                      sx={{ color: abyssColors.primary[500] }}
                    >
                      {filteredItems.length}
                    </Typography>
                  )}
                </ListItemButton>
              </ListItem>
              <Divider sx={{ mb: 1 }} />
            </>
          )}
          {filteredItems.map((item: NavigationListItem) => (
            <ListItemButton
              sx={listButtonStyles}
              key={item.id}
              selected={selectedItemId === item.id}
              onClick={() => item.handleClick()}
            >
              <Tooltip title={item.label}>
                <Typography variant="body1" color={abyssColors.primary[500]}>
                  {item.label}
                </Typography>
              </Tooltip>
              <Typography variant="body1" color={abyssColors.primary[500]}>
                {item.count}
              </Typography>
            </ListItemButton>
          ))}
        </List>
      </ScrollableList>
    </ListBox>
  );
};
